import { useMemo, useCallback, useRef, useState, useEffect } from 'react';
import './Trend.css';

import { Text } from '@visx/text';
import { LinePath, Bar, Circle } from '@visx/shape';
import { Group } from '@visx/group';
import { scaleLinear } from '@visx/scale';
import { curveBasis } from '@visx/curve';
import { localPoint } from '@visx/event';
import { useTooltip } from '@visx/tooltip';
import { bisector } from 'd3-array';
import { timeFormat } from 'd3-time-format';
import { set, throttle } from 'lodash';
import { AreaClosed } from '@visx/shape';

import { useSensorData } from './useSensorData';
import TrendPeakGradient from './TrendPeakGradient';
import TrendLimits from './TrendLimits';
import TrendGrid from './TrendGrid';
import TrendTooltip, { LineAndDot } from './TrendTooltip';
import TrendLoading from './TrendLoading';
import useTimeScale from './useTimeScale';
import { computeScaleBounds } from './scaleBounds';

const margin = {
  top: 20,
  bottom: 15,
  left: 0,
  right: 0,
};

const getSensorDate = (d) => d.date;
const getSensorValue = (d) => d.value;
const getDynLimValue = (d) => d.value;
const getLast = (arr) => {
  return arr[arr.length - 1];
};

// const getDynLimLowBound = (d) => d.loLimit;
// const getDynLimHighBound = (d) => d.hiLimit;

const isFiniteNumber = (v) => typeof v === 'number' && Number.isFinite(v);

const pickLimit = (limits, key, severity) => {
  // supports both {low: 36, high: 44} and [{severity:'low',limit:36}, ...]
  if (Array.isArray(limits)) {
    return limits.find((l) => l?.severity === severity)?.limit ?? null;
  }
  if (limits && typeof limits === 'object') {
    if (isFiniteNumber(limits[key])) return limits[key];
    if (isFiniteNumber(limits[severity])) return limits[severity];
  }
  return null;
};

const bisectDate = bisector((d) => d.date).left;
const formatDate = timeFormat('%H:%M');

function Trend({
  sensorName,
  limits,
  height,
  width,
  id,
  hideLimitLabels = false,
  hideGrid = true,
  unit,
  chartVisible,
}) {
  const xMax = width - margin.left - margin.right;
  const yMax = height - margin.top - margin.bottom;

  const dateScale = useTimeScale(xMax);
  const [dynLimTooltipData, setDynLimTooltipData] = useState(null);

  const [sensorData, minValue, maxValue, dynLimData] = useSensorData(sensorName, limits);
  const trendRef = useRef(null);

  const [shouldContinueLoadingAnimation, setShouldContinueLoadingAnimation] = useState(true);
  const { showTooltip, hideTooltip, tooltipData, tooltipTop, tooltipLeft, tooltipOpen } =
    useTooltip();

  // hard limits (works with {low,high} or [{severity,limit}, ...])
  const hardLow = useMemo(() => pickLimit(limits, 'low', 'low'), [limits]);
  const hardHigh = useMemo(() => pickLimit(limits, 'high', 'high'), [limits]);

  // dynamic accessors with fallback to hard limit (or null if no hard limit)
  const getDynLimLowBound = useCallback(
    (d) => {
      const v = isFiniteNumber(d?.loLimit) ? d.loLimit : d?.proj_lo;
      if (isFiniteNumber(v)) return v;
      return isFiniteNumber(hardLow) ? hardLow : null;
    },
    [hardLow]
  );

  const getDynLimHighBound = useCallback(
    (d) => {
      const v = isFiniteNumber(d?.hiLimit) ? d.hiLimit : d?.proj_hi;
      if (isFiniteNumber(v)) return v;
      return isFiniteNumber(hardHigh) ? hardHigh : null;
    },
    [hardHigh]
  );

  useEffect(() => {
    let timer = setTimeout(() => {
      setShouldContinueLoadingAnimation(false);
    }, 3000);

    return () => {
      clearTimeout(timer);
    };
  }, [chartVisible]);

  const allValues = useMemo(() => {
    const sensorValues = sensorData.map((d) => d.value).filter(Number.isFinite);
    const dynLoValues = dynLimData.map((d) => getDynLimLowBound(d)).filter(Number.isFinite);
    const dynHiValues = dynLimData.map((d) => getDynLimHighBound(d)).filter(Number.isFinite);
    return [...sensorValues, ...dynLoValues, ...dynHiValues];
  }, [sensorData, dynLimData, getDynLimLowBound, getDynLimHighBound]);

  const { combinedMin, combinedMax } = useMemo(
    () => computeScaleBounds(allValues, hardLow, hardHigh),
    [allValues, hardLow, hardHigh]
  );
  const pad =
    combinedMax === combinedMin ? (combinedMax === 0 ? 1 : Math.abs(combinedMax) * 0.01) : 0;

  const sensorValueScale = useMemo(
    () =>
      scaleLinear({
        range: [yMax, 0],
        domain: [combinedMin - pad, combinedMax + pad],
        nice: true,
      }),
    [yMax, combinedMin, combinedMax, pad]
  );

  function updateTooltip(d, x, l) {
    setDynLimTooltipData(l);
    showTooltip({
      tooltipData: d,
      tooltipLeft: x,
      tooltipTop: sensorValueScale(getSensorValue(d)),
    });
  }

  //   const getDynLimScale = useMemo(() => {
  //     return scaleLinear({
  //       range: [yMax, 0],
  //       domain: [boundLow || 0, boundHigh || 0],
  //       nice: true,
  //     });
  //   }, [yMax, boundHigh, boundLow]);

  const debouncedTooltip = useCallback(throttle(updateTooltip, 50), [sensorData]);

  const handleTooltip = useCallback(
    (event) => {
      const { x } = localPoint(event) || { x: 0 };
      const x0 = dateScale.invert(x);

      // Find the closest sensor data point by date
      const sensorIndex = bisectDate(sensorData, x0, 1);
      const safeSensorIndex = Math.max(0, Math.min(sensorIndex, sensorData.length - 1));
      const sensorDatum = sensorData[safeSensorIndex];

      // Find the closest dynLimData point by date
      let closestDynLimDatum = null;
      if (dynLimData && dynLimData.length > 0 && sensorDatum) {
        const sensorDate = getSensorDate(sensorDatum);
        closestDynLimDatum = dynLimData.reduce((prev, curr) => {
          return Math.abs(getSensorDate(curr) - sensorDate) <
            Math.abs(getSensorDate(prev) - sensorDate)
            ? curr
            : prev;
        }, dynLimData[0]);
      }

      debouncedTooltip(sensorDatum, x, closestDynLimDatum);
    },
    [sensorData, dynLimData, sensorValueScale, dateScale]
  );

  const gradientId = `gradient-${id}`;

  function isDynLimPointDefined(d) {
    return Number.isFinite(getSensorDate(d));
  }

  return (
    <div className="trend" ref={trendRef}>
      <svg className="trend__svg" width={width} height={height}>
        {sensorData.length === 0 && width && shouldContinueLoadingAnimation && (
          <TrendLoading width={width} height={height} />
        )}
        {sensorData.length === 0 && !shouldContinueLoadingAnimation && (
          <>
            <svg height="100%" width="100%" style={{ overflow: 'hidden' }}>
              <Text
                className="trend__limits-text trend__no-data"
                x={width / 2}
                y={height / 2}
                verticalAnchor="middle"
                textAnchor="middle"
                stroke="#f2f2f2"
                fill="#f2f2f2"
                fontSize="14"
              >
                No data has been received within the last 30 minutes
              </Text>
            </svg>
          </>
        )}
        {sensorData.length > 0 && (
          <>
            <defs>
              <TrendPeakGradient
                limits={limits}
                minValue={minValue}
                maxValue={maxValue}
                id={gradientId}
              />
              {/* Low limit icon - arrow pointing down with dots (grey) */}
              <symbol id="low" viewBox="0 0 24 24">
                <polygon
                  points="8.46 7.64 12 11.17 15.54 7.64 16.95 9.05 12 14 7.05 9.05 8.46 7.64"
                  fill="var(--color-gray, #9a989f)"
                />
                <rect x="11" y="15" width="2" height="2" fill="var(--color-gray, #9a989f)" />
                <rect x="7" y="15" width="2" height="2" fill="var(--color-gray, #9a989f)" />
                <rect x="15" y="15" width="2" height="2" fill="var(--color-gray, #9a989f)" />
              </symbol>
              {/* High limit icon - arrow pointing up with dots (grey) */}
              <symbol id="high" viewBox="0 0 24 24">
                <polygon
                  points="15.54 16.36 12 12.83 8.46 16.36 7.05 14.95 12 10 16.95 14.95 15.54 16.36"
                  fill="var(--color-gray, #9a989f)"
                />
                <rect x="11" y="7" width="2" height="2" fill="var(--color-gray, #9a989f)" />
                <rect x="15" y="7" width="2" height="2" fill="var(--color-gray, #9a989f)" />
                <rect x="7" y="7" width="2" height="2" fill="var(--color-gray, #9a989f)" />
              </symbol>
            </defs>
            <Group key={`lines`} top={margin.top}>
              {!hideGrid && (
                <TrendGrid
                  width={xMax}
                  height={yMax}
                  scale={dateScale}
                  formatFunction={formatDate}
                  numTicks={12}
                />
              )}
              <svg height="100%" width="100%" style={{ overflow: 'hidden' }}>
                {/* Line for dynamic maxValue */}
                <LinePath
                  className="trend__path"
                  curve={curveBasis}
                  data={sensorData}
                  x={(d) => dateScale(getSensorDate(d))}
                  y={(d) => sensorValueScale(getSensorValue(d))}
                  stroke={`url(#${gradientId})`}
                  strokeWidth="1"
                  strokeOpacity="1"
                  shapeRendering="geometricPrecision"
                />
              </svg>

              <svg height="100%" width="100%" style={{ overflow: 'hidden' }}>
                {/* Line for dynamic minValue */}
                <LinePath
                  className="hiLim__path"
                  curve={curveBasis}
                  data={dynLimData}
                  defined={(d, i, arr) =>
                    Number.isFinite(getDynLimHighBound(d)) && isDynLimPointDefined(d, i, arr)
                  }
                  x={(d) => dateScale(getSensorDate(d))}
                  y={(d) => sensorValueScale(getDynLimHighBound(d))}
                  stroke={`#FFFF66`}
                  strokeWidth="1.5"
                  strokeOpacity="1"
                  strokeDasharray="2,3"
                  shapeRendering="geometricPrecision"
                />
              </svg>
              <svg height="100%" width="100%" style={{ overflow: 'hidden' }}>
                <LinePath
                  className="loLim__path"
                  curve={curveBasis}
                  data={dynLimData}
                  defined={(d, i, arr) =>
                    Number.isFinite(getDynLimLowBound(d)) && isDynLimPointDefined(d, i, arr)
                  }
                  x={(d) => dateScale(getSensorDate(d))}
                  y={(d) => sensorValueScale(getDynLimLowBound(d))}
                  stroke={`#FFFF66`}
                  strokeWidth="1.5"
                  strokeOpacity="1"
                  strokeDasharray="2,3"
                  shapeRendering="geometricPrecision"
                />
              </svg>
              {Number.isFinite(hardHigh) && (
                <svg height="100%" width="100%" style={{ overflow: 'hidden' }}>
                  <AreaClosed
                    data={dynLimData}
                    defined={(d, i, arr) =>
                      Number.isFinite(getDynLimHighBound(d)) && isDynLimPointDefined(d, i, arr)
                    }
                    x={(d) => dateScale(getSensorDate(d))}
                    // draw between the two values regardless of which is larger
                    y={(d) => sensorValueScale(Math.max(hardHigh, getDynLimHighBound(d)))}
                    y0={(d) => sensorValueScale(Math.min(hardHigh, getDynLimHighBound(d)))}
                    stroke="none"
                    fill="var(--color-warning, #f59e0b)" // orange fallback
                    fillOpacity={0.28}
                    curve={curveBasis}
                    pointerEvents="none"
                  />
                </svg>
              )}

              {/* --- Orange wedge toward LOW: shade between hard LOW and dynamic LOW --- */}
              {Number.isFinite(hardLow) && (
                <svg height="100%" width="100%" style={{ overflow: 'hidden' }}>
                  <AreaClosed
                    data={dynLimData}
                    defined={(d, i, arr) =>
                      Number.isFinite(getDynLimLowBound(d)) && isDynLimPointDefined(d, i, arr)
                    }
                    x={(d) => dateScale(getSensorDate(d))}
                    y={(d) => sensorValueScale(Math.max(hardLow, getDynLimLowBound(d)))}
                    y0={(d) => sensorValueScale(Math.min(hardLow, getDynLimLowBound(d)))}
                    stroke="none"
                    fill="var(--color-warning, #f59e0b)"
                    fillOpacity={0.28}
                    curve={curveBasis}
                    pointerEvents="none"
                  />
                </svg>
              )}
              {sensorData.length <= 0 && (
                <Text
                  className="trend__limits-text trend__no-data"
                  x={width / 2}
                  y={height / 2}
                  verticalAnchor="middle"
                  textAnchor="middle"
                  stroke="#f2f2f2"
                  fill="#f2f2f2"
                  fontSize="14"
                >
                  No new data was received in the last 30 minutes
                </Text>
              )}
              <Circle
                cx={dateScale(getSensorDate(getLast(sensorData)))}
                cy={sensorValueScale(getSensorValue(getLast(sensorData)))}
                r={2}
                fill="white"
              />

              <TrendLimits
                hideLimitLabels={hideLimitLabels}
                limits={limits}
                scale={sensorValueScale}
                xMax={xMax}
                yMax={yMax}
                unit={unit}
              />
            </Group>
            <Bar
              x={0}
              y={0}
              width={xMax}
              height={height}
              fill="transparent"
              rx={14}
              onMouseMove={handleTooltip}
              onMouseLeave={() => hideTooltip()}
            />{' '}
            {tooltipData && tooltipOpen && (
              <LineAndDot top={tooltipTop + margin.top} left={tooltipLeft} height={height} />
            )}{' '}
          </>
        )}{' '}
      </svg>
      {sensorData.length > 0 && tooltipData && tooltipOpen && (
        <TrendTooltip
          data={tooltipData}
          top={tooltipTop + 10}
          left={tooltipLeft}
          limits={limits}
          unit={unit}
          dynLimData={dynLimTooltipData}
        />
      )}{' '}
    </div>
  );
}

export default Trend;
